/**
 * How this workspace makes the meeting link for an online appointment.
 *
 * Four calls, two of which take the provider in the path:
 *
 *   `GET   /meeting-settings/`                    reads it
 *   `PATCH /meeting-settings/`                    sets it — not `POST`, which
 *                                                 the endpoint refuses
 *   `POST  /meeting-providers/{slug}/connect/`    starts the sign-in
 *   `POST  /meeting-providers/{slug}/callback/`   finishes it
 *
 * Setting and linking are two different things, and the screen treats them so.
 * Linking signs into an account at Google or Zoom and is a trip out of the
 * app; setting is a field on this workspace — `GOOGLE_MEET`, `ZOOM` or
 * `MANUAL` — that can be flipped back and forth without signing in again. A
 * workspace that has linked an account and then turned that provider off
 * still has the account.
 *
 * Because the setting is one field, it holds one provider: switching to Zoom
 * is what turns Google Meet off. The accounts are not one field, and both can
 * be linked at once — which is why `providers` below is a list and the two
 * questions the card asks ("linked?" and "on?") have different answers.
 *
 * Linking takes two calls because the provider sits in the middle of it. The
 * first hands back a URL; the browser leaves, signs in, and comes back to
 * this app carrying a `code`; the second trades that code for the tokens the
 * backend keeps. Nothing in between is this app's to hold — not even Zoom's
 * PKCE verifier, which is the backend's half of the `code_challenge` it put
 * in that URL.
 *
 * The wire says `providers[].connection` where this side says `linked`: a
 * connection object means an account is on file and `null` means none is.
 * `available` tracks the same thing, so it is read but not relied on alone —
 * see `toProviderLink`.
 */

import { api, API_ENDPOINTS } from "../../../lib/api";
import type { GetToken } from "../../auth/types/auth.types";
import type {
  MeetingProviderCode,
  MeetingProviderLink,
  MeetingProviderResponse,
  MeetingProviderSlug,
  MeetingSettings,
  MeetingSettingsPayload,
  MeetingSettingsResponse,
  ProviderConnectResponse,
} from "../types/appointments.types";

const token = async (getToken: GetToken, what: string): Promise<string> => {
  const value = await getToken();
  if (!value) {
    // Not an ApiError: the server was never asked. Calling this without an
    // active session is a bug at the call site, not something to show a user.
    throw new Error(`No active Clerk session - cannot ${what}.`);
  }
  return value;
};

/**
 * The two halves of the handshake, per provider.
 *
 * Tables rather than a path built by hand from the slug: these are the only
 * two providers there are, the endpoints are written out in `API_ENDPOINTS`
 * where every other path in the app is, and a `${slug}` interpolated here
 * would put a second spelling of them in a second file.
 */
const CONNECT: Record<MeetingProviderSlug, string> = {
  google: API_ENDPOINTS.appointments.googleConnect,
  zoom: API_ENDPOINTS.appointments.zoomConnect,
};

const CALLBACK: Record<MeetingProviderSlug, string> = {
  google: API_ENDPOINTS.appointments.googleCallback,
  zoom: API_ENDPOINTS.appointments.zoomCallback,
};

/** For the sentences below, which name the provider somebody is waiting on. */
const NAMES: Record<MeetingProviderSlug, string> = {
  google: "Google Meet",
  zoom: "Zoom",
};

/**
 * One provider as the card reads it.
 *
 * `linked` is taken from the connection rather than from `available`, and
 * wants the status too: a connection that has gone stale — an account whose
 * consent was withdrawn on the provider's side — is still an object, and a
 * card that called it linked would offer "Disconnect" where the only way
 * forward is signing in again.
 */
const toProviderLink = (provider: MeetingProviderResponse): MeetingProviderLink => ({
  provider: provider.provider,
  slug: provider.slug,
  label: provider.label,
  available: provider.available,
  linked: provider.connection?.status === "ACTIVE",
  accountEmail: provider.connection?.account_email ?? null,
});

/**
 * Anything the backend has not named reads as `MANUAL`, which is the setting
 * that promises least: no link is made, and the appointment carries whatever
 * it was given. Guessing a provider for a word this app has never seen would
 * have the dialog stop asking for a link on the strength of a provider nobody
 * here knows how to use, and wear a logo it has no file for.
 */
const toProviderCode = (code: string): MeetingProviderCode =>
  code === "GOOGLE_MEET" || code === "ZOOM" ? code : "MANUAL";

/**
 * The flags, where the backend sent them, and the setting where it did not.
 *
 * It sends only the flags it has something to say about: a workspace on
 * `GOOGLE_MEET` comes back with no `zoom_enabled` key at all rather than with
 * `false`. Reading a missing key as `false` is right there and wrong on the
 * day the same body arrives for a workspace on `ZOOM` — so the key is read as
 * no answer, and `meeting_provider`, which is always sent, decides.
 */
const toMeetingSettings = (response: MeetingSettingsResponse): MeetingSettings => {
  const provider = toProviderCode(response.meeting_provider);

  return {
    provider,
    googleMeetEnabled: response.google_meet_enabled ?? provider === "GOOGLE_MEET",
    zoomEnabled: response.zoom_enabled ?? provider === "ZOOM",
    providers: (response.providers ?? []).map(toProviderLink),
  };
};

/** What this workspace has set, and what it has linked. */
export async function fetchMeetingSettings(
  getToken: GetToken,
  signal?: AbortSignal
): Promise<MeetingSettings> {
  return toMeetingSettings(
    await api.get<MeetingSettingsResponse>(
      API_ENDPOINTS.appointments.meetingSettings,
      { token: await token(getToken, "read the meeting settings"), signal }
    )
  );
}

/**
 * Sets what makes the link — the on and the off both, and the switch from one
 * provider to the other, which is the same call: `meeting_provider` is one
 * field, so naming Zoom is what stops Google Meet.
 *
 * It answers with the settings as they now stand, so the caller replaces what
 * it holds with what came back rather than with what it asked for.
 */
export async function saveMeetingProvider(
  getToken: GetToken,
  provider: MeetingProviderCode,
  signal?: AbortSignal
): Promise<MeetingSettings> {
  const body: MeetingSettingsPayload = { meeting_provider: provider };

  return toMeetingSettings(
    await api.patch<MeetingSettingsResponse>(
      API_ENDPOINTS.appointments.meetingSettings,
      body,
      { token: await token(getToken, "change the meeting settings"), signal }
    )
  );
}

/**
 * Starts a provider's sign-in, and hands back the URL to send the browser to.
 *
 * It does not navigate. Leaving the app is the screen's decision to make —
 * there may be a dialog to close or a word to say first — and a service that
 * moved the browser by itself could not be called from anywhere that wanted
 * to do either.
 */
export async function startProviderConnect(
  getToken: GetToken,
  slug: MeetingProviderSlug,
  signal?: AbortSignal
): Promise<string> {
  const response = await api.post<ProviderConnectResponse>(
    CONNECT[slug],
    undefined,
    { token: await token(getToken, `connect ${NAMES[slug]}`), signal }
  );

  const url = response?.authorization_url;
  if (!url) {
    // Nothing to send the browser to. Thrown rather than returned empty so
    // the screen says something went wrong, instead of a press that silently
    // does nothing.
    throw new Error(`The server did not return a ${NAMES[slug]} sign-in link.`);
  }
  return url;
}

/**
 * Finishes the sign-in: trades what the provider sent back for a linked
 * account.
 *
 * `code` and `state` are read off this app's own URL, because the provider
 * returns to this app rather than to the backend — see `googleConnect` and
 * `zoomConnect`. They are passed straight through and never stored: a code is
 * good once, for a few minutes, and only the backend can spend it.
 *
 * It does not turn the provider on. Linking an account and using it are two
 * settings, and the caller makes the second call itself.
 */
export async function completeProviderConnect(
  getToken: GetToken,
  slug: MeetingProviderSlug,
  code: string,
  state: string,
  signal?: AbortSignal
): Promise<void> {
  await api.post<unknown>(
    CALLBACK[slug],
    { code, state },
    { token: await token(getToken, `finish connecting ${NAMES[slug]}`), signal }
  );
}
